import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarDays, LoaderCircle, Moon, Sun, Sunrise, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  DAY_PERIOD_ORDER,
  DAY_PERIODS,
  dayPeriodAvailability,
  describeDay,
  jumpScrollTop,
  relaxedSpacer,
  spacerNeeded,
  type DayPeriod,
  type DayPeriodSection,
} from "@/lib/dayPeriods";
import { holdHeader } from "@/lib/headerScrollHold";
import { trackEvent } from "@/lib/analytics";
import { toast } from "@/hooks/use-toast";

const PERIOD_ICONS: Record<DayPeriod, LucideIcon> = {
  allDay: CalendarDays,
  morning: Sunrise,
  afternoon: Sun,
  evening: Moon,
};

/** Space between the bar and a section heading after a jump. */
const JUMP_GAP = 10;
/** A section counts as the one being read once its heading is this close under the bar. */
const SPY_REACH = JUMP_GAP + 10;
/** After landing, keep the section in place this long while nearby cards finish drawing. */
const HOLD_MS = 900;

export function dayPeriodAnchorId(dateString: string, period: DayPeriod): string {
  return `day-${dateString}-${period}`;
}

/** Full-width separator inside a day's event grid, e.g. "☀ AFTERNOON  12–5 PM · 20 events" over a rule. */
export function DayPeriodHeading({ dateString, section }: { dateString: string; section: DayPeriodSection }) {
  const { label, range } = DAY_PERIODS[section.period];
  const Icon = PERIOD_ICONS[section.period];
  return (
    <div
      id={dayPeriodAnchorId(dateString, section.period)}
      className={`day-period-heading is-${section.period} col-span-full`}
      tabIndex={-1}
      data-day-period-anchor={section.period}
      data-testid={`day-period-${section.period}`}
    >
      <Icon className="day-period-icon" aria-hidden="true" />
      <span className="day-period-label">{label}</span>
      {/* One piece, so on a narrow phone it wraps below the label as a whole */}
      <span className="day-period-meta">
        {range} &middot; {section.count} {section.count === 1 ? "event" : "events"}
      </span>
    </div>
  );
}

/**
 * The start of each day in the list: a calendar tile with the date, then the day's name,
 * e.g. [SEP 28] "Tomorrow" / "Monday · 83 events". Bigger than the time-of-day headings
 * below it, so the day being read is easy to spot while scrolling.
 */
export function DayHeading({
  dateString,
  count,
  headingRef,
}: {
  dateString: string;
  count: number;
  headingRef?: (element: HTMLDivElement | null) => void;
}) {
  const day = describeDay(dateString);
  const countLabel = `${count} ${count === 1 ? "event" : "events"}`;
  return (
    <div ref={headingRef} className="day-heading" data-date={dateString} data-testid={`date-separator-${dateString}`}>
      <span className="day-heading-tile" aria-hidden="true">
        <span className="day-heading-tile-month">{day.monthShort}</span>
        <span className="day-heading-tile-day">{day.dayOfMonth}</span>
      </span>
      <h2 className="day-heading-text">
        {/* The date is in the tile; screen readers get it here instead */}
        <span className="day-heading-title">
          {day.relative ?? day.weekday}
          <span className="sr-only">, {day.monthDay}</span>
        </span>
        <span className="day-heading-detail">
          {day.relative ? <>{day.weekday} &middot; </> : null}
          {countLabel}
        </span>
      </h2>
    </div>
  );
}

/** "Today", "Tomorrow" or "Wed, Sep 30". */
function dayLabelFor(dateString: string): string {
  const day = describeDay(dateString);
  return day.relative ?? `${day.weekdayShort}, ${day.monthShort} ${day.dayOfMonth}`;
}

/** "today", "tomorrow" or "on Wed, Sep 30", to finish a sentence. */
function dayPhraseFor(dateString: string): string {
  const label = dayLabelFor(dateString);
  return label === "Today" || label === "Tomorrow" ? label.toLowerCase() : `on ${label}`;
}

/** "evening events", or "events without a set time" (not "no set time events"). */
function periodPhrase(period: DayPeriod): string {
  return period === "allDay" ? "events without a set time" : `${DAY_PERIODS[period].label.toLowerCase()} events`;
}

/** Short form for phones: "Today", "Tmrw", "Wed". */
function shortDayLabelFor(dateString: string): string {
  const day = describeDay(dateString);
  if (day.relative === "Tomorrow") return "Tmrw";
  return day.relative ?? day.weekdayShort;
}

// ─── Bottom spacer ───────────────────────────────────────────────────────────
// Near the end of the loaded list there may not be a screen's worth of events below a
// section, so the page can't scroll far enough to put it under the header. The spacer adds
// that room during a jump, then shrinks as the visitor scrolls up or more events load.

let spacerEl: HTMLDivElement | null = null;

function spacerHeight(): number {
  return spacerEl ? parseFloat(spacerEl.style.height) || 0 : 0;
}

function setSpacerHeight(px: number) {
  if (spacerEl) spacerEl.style.height = `${Math.max(0, px)}px`;
}

function ensureRoomFor(destination: number) {
  const needed = spacerNeeded(destination, document.documentElement.scrollHeight, window.innerHeight, spacerHeight());
  if (needed > spacerHeight()) setSpacerHeight(needed);
}

function relaxSpacer() {
  const current = spacerHeight();
  if (!current) return;
  const next = relaxedSpacer(current, document.documentElement.scrollHeight, window.innerHeight, window.scrollY);
  if (next !== current) setSpacerHeight(next);
}

/** Render once, after the event list (and its "Loading more" row). */
export function DayPeriodJumpSpacer() {
  return (
    <div
      ref={(el) => {
        spacerEl = el;
      }}
      className="day-period-jump-spacer"
      aria-hidden="true"
      data-testid="day-period-jump-spacer"
    />
  );
}

// ─── Scrolling ───────────────────────────────────────────────────────────────

// Timers rather than animation frames: frames stop when the page is in the background
// (switching apps mid-jump), and the jump must still finish.
const TICK_MS = 16;
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Resolves when the page has reached `destination` and stopped, or stopped moving for good.
 * Timed in milliseconds rather than frames, so a slow or busy phone waits the same.
 */
function waitForScrollToSettle(destination: number, signal: { aborted: boolean }): Promise<void> {
  return new Promise((resolve) => {
    const started = performance.now();
    let last = window.scrollY;
    let lastMove = started;
    let moved = false;
    const tick = () => {
      if (signal.aborted) return resolve();
      const now = performance.now();
      const y = window.scrollY;
      if (Math.abs(y - last) >= 0.5) {
        moved = true;
        lastMove = now;
        last = y;
      }
      const quietFor = now - lastMove;
      const arrived = Math.abs(y - destination) <= 1;
      if ((arrived && quietFor >= 50) || (moved && quietFor >= 150) || (!moved && quietFor >= 400) || now - started > 3000) {
        resolve();
      } else {
        setTimeout(tick, TICK_MS);
      }
    };
    setTimeout(tick, TICK_MS);
  });
}

// ─── The bar ─────────────────────────────────────────────────────────────────

interface DayPeriodJumpBarProps {
  /** Loaded days, in list order */
  dates: string[];
  /** The times of day each loaded day has, in list order */
  periodsByDate: ReadonlyMap<string, readonly DayPeriod[]>;
  /** Changes whenever the search or filters change */
  listInstanceId: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoadError: boolean;
  onLoadMore: () => Promise<unknown>;
}

type Pin = { date: string; period: DayPeriod | null };

/**
 * One sticky "Jump to" bar for the whole list. It names the day on screen and highlights
 * the time of day being read (nothing at the start of the list). Tapping a time of day
 * scrolls its section up under the header, loading more events first when needed;
 * tapping the highlighted one again goes back to the start of the list.
 *
 * Render it right before the list, inside the element that holds the list, and render
 * DayPeriodJumpSpacer once after the list.
 */
export function DayPeriodJumpBar({
  dates,
  periodsByDate,
  listInstanceId,
  hasNextPage,
  isFetchingNextPage,
  isLoadError,
  onLoadMore,
}: DayPeriodJumpBarProps) {
  const barRef = useRef<HTMLElement>(null);
  /** Marks where the bar sits in the page before it sticks: the start of the list */
  const startRef = useRef<HTMLDivElement>(null);
  const [spot, setSpot] = useState<{ date: string | null; period: DayPeriod | null }>({ date: null, period: null });
  /** While a jump is scrolling, the bar shows its destination instead of every section passed on the way */
  const [pin, setPin] = useState<Pin | null>(null);
  const [pending, setPending] = useState<{ date: string; period: DayPeriod } | null>(null);
  const [status, setStatus] = useState("");
  const jumpRef = useRef<{ aborted: boolean } | null>(null);
  const loadRequestedRef = useRef(false);

  const measure = useCallback(() => {
    if (jumpRef.current) return;
    const bar = barRef.current;
    const container = bar?.parentElement;
    if (!bar || !container) return;
    const cutoff = bar.getBoundingClientRect().bottom + SPY_REACH;
    const groups = container.querySelectorAll<HTMLElement>("[data-day-group]");
    let group: HTMLElement | null = groups[0] ?? null;
    for (const candidate of groups) {
      if (candidate.getBoundingClientRect().top > cutoff) break;
      group = candidate;
    }
    let period: DayPeriod | null = null;
    if (group) {
      for (const anchor of group.querySelectorAll<HTMLElement>("[data-day-period-anchor]")) {
        if (anchor.getBoundingClientRect().top > cutoff) break;
        period = anchor.dataset.dayPeriodAnchor as DayPeriod;
      }
    }
    const date = group?.dataset.dayGroup ?? null;
    setSpot((current) => (current.date === date && current.period === period ? current : { date, period }));
  }, []);

  // Follow the visitor's scrolling, and let the spacer go once it is no longer needed
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      measure();
      if (!jumpRef.current) relaxSpacer();
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    // The bar moves when the phone header hides or returns (the header sets --header-offset on
    // <html>), sliding unless motion is reduced: measure on the change and after the slide
    const bar = barRef.current;
    bar?.addEventListener("transitionend", schedule);
    const headerObserver = typeof MutationObserver !== "undefined" ? new MutationObserver(schedule) : null;
    headerObserver?.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });
    const container = bar?.parentElement;
    const resizeObserver = container && typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    if (container) resizeObserver?.observe(container);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      bar?.removeEventListener("transitionend", schedule);
      headerObserver?.disconnect();
      resizeObserver?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [measure, dates]);

  const cancelJump = useCallback(() => {
    if (jumpRef.current) jumpRef.current.aborted = true;
    jumpRef.current = null;
  }, []);

  // A new search or filter is a new list: drop any jump in progress and the extra space
  useEffect(() => {
    cancelJump();
    setPin(null);
    setPending(null);
    setStatus("");
    setSpacerHeight(0);
  }, [listInstanceId, cancelJump]);

  useEffect(() => () => {
    cancelJump();
    setSpacerHeight(0);
  }, [cancelJump]);

  /** Where the bottom of the bar is once it has stuck under the header */
  const stuckBarBottom = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return 0;
    return (parseFloat(getComputedStyle(bar).top) || 0) + bar.getBoundingClientRect().height;
  }, []);

  const sectionDestination = useCallback((date: string, period: DayPeriod) => () => {
    const target = document.getElementById(dayPeriodAnchorId(date, period));
    if (!target) return null;
    return jumpScrollTop(window.scrollY, target.getBoundingClientRect().top, stuckBarBottom(), JUMP_GAP);
  }, [stuckBarBottom]);

  /** Scroll position where the bar reaches the header but has not covered anything yet */
  const listStartDestination = useCallback(() => {
    const start = startRef.current;
    const bar = barRef.current;
    if (!start || !bar) return null;
    const style = getComputedStyle(bar);
    const marginTop = parseFloat(style.marginTop) || 0;
    const stickyTop = parseFloat(style.top) || 0;
    return Math.max(0, Math.round(window.scrollY + start.getBoundingClientRect().top + marginTop - stickyTop));
  }, []);

  /**
   * Smooth-scrolls to a destination that is recomputed from the live layout: cards drawn on
   * the way can change height, and the list may grow. Settles exactly, then holds briefly.
   * Wheel, touch or a key press hands control back to the visitor at once.
   */
  const scrollWithBar = useCallback(async (nextPin: Pin, destination: () => number | null, focusId?: string) => {
    cancelJump();
    const signal = { aborted: false };
    jumpRef.current = signal;
    setPin(nextPin);
    const releaseHeader = holdHeader();
    const stop = () => {
      signal.aborted = true;
    };
    const stopOnPointer = (event: PointerEvent) => {
      // A tap on the bar starts a new jump instead
      if (!barRef.current?.contains(event.target as Node)) stop();
    };
    const stopEvents = ["wheel", "touchstart", "keydown"] as const;
    stopEvents.forEach((type) => window.addEventListener(type, stop, { capture: true, passive: true }));
    window.addEventListener("pointerdown", stopOnPointer, { capture: true, passive: true });

    try {
      const first = destination();
      if (first === null) return;
      ensureRoomFor(first);
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: first, behavior: reduceMotion ? "auto" : "smooth" });
      await waitForScrollToSettle(first, signal);

      const holdUntil = performance.now() + HOLD_MS;
      let focused = false;
      while (!signal.aborted) {
        const target = destination();
        if (target === null) break;
        if (Math.abs(target - window.scrollY) > 1) {
          ensureRoomFor(target);
          window.scrollTo({ top: target, behavior: "auto" });
        } else if (!focused && focusId) {
          document.getElementById(focusId)?.focus({ preventScroll: true });
          focused = true;
        }
        if (performance.now() >= holdUntil) break;
        await sleep(TICK_MS);
      }
    } finally {
      stopEvents.forEach((type) => window.removeEventListener(type, stop, { capture: true }));
      window.removeEventListener("pointerdown", stopOnPointer, { capture: true });
      releaseHeader();
      if (jumpRef.current === signal) {
        jumpRef.current = null;
        setPin(null);
        requestAnimationFrame(measure);
      }
    }
  }, [cancelJump, measure]);

  const jumpToSection = useCallback((date: string, period: DayPeriod) => {
    setStatus("");
    void scrollWithBar({ date, period }, sectionDestination(date, period), dayPeriodAnchorId(date, period));
  }, [scrollWithBar, sectionDestination]);

  const reportNoEvents = useCallback((date: string, period: DayPeriod) => {
    const message = `No ${periodPhrase(period)} ${dayPhraseFor(date)}.`;
    setStatus(message);
    toast({ title: message });
  }, []);

  // Keep loading pages until the requested section is in the list, then jump to it
  useEffect(() => {
    if (!pending) return;
    const availability = dayPeriodAvailability(pending.period, pending.date, periodsByDate, dates, hasNextPage);
    if (availability === "available") {
      setPending(null);
      jumpToSection(pending.date, pending.period);
    } else if (availability === "none") {
      setPending(null);
      reportNoEvents(pending.date, pending.period);
    } else if (isFetchingNextPage) {
      // wait for the page on its way
    } else if (isLoadError && loadRequestedRef.current) {
      setPending(null);
      setStatus("Could not load more events. Please try again.");
      toast({ title: "Could not load more events", description: "Please try the jump again." });
    } else {
      loadRequestedRef.current = true;
      void onLoadMore();
    }
  }, [pending, periodsByDate, dates, hasNextPage, isFetchingNextPage, isLoadError, onLoadMore, jumpToSection, reportNoEvents]);

  const date = pin?.date ?? spot.date ?? dates[0] ?? "";
  const highlighted = pin ? pin.period : spot.period;

  const handleTap = (period: DayPeriod) => {
    if (!date) return;
    // Tapping the loading button again cancels the jump
    if (pending && pending.period === period) {
      setPending(null);
      setStatus("");
      trackEvent("day_period_jump", { period, action: "cancel" });
      return;
    }

    // Tapping the highlighted time of day again unselects it: back to the start of the list
    if (!pending && highlighted === period) {
      trackEvent("day_period_jump", { period, action: "top" });
      setStatus("Back to the start of the list.");
      void scrollWithBar({ date: dates[0] ?? date, period: null }, listStartDestination);
      return;
    }

    const availability = dayPeriodAvailability(period, date, periodsByDate, dates, hasNextPage);
    trackEvent("day_period_jump", { period, action: availability });
    if (availability === "available") {
      setPending(null);
      jumpToSection(date, period);
    } else if (availability === "loadable") {
      cancelJump();
      setPin(null);
      loadRequestedRef.current = false;
      setPending({ date, period });
      setStatus(`Loading ${periodPhrase(period)} for ${dayLabelFor(date)}.`);
    } else {
      setPending(null);
      reportNoEvents(date, period);
    }
  };

  if (!date) return null;
  const dayLabel = dayLabelFor(date);
  const day = describeDay(date);

  return (
    <>
      <div ref={startRef} aria-hidden="true" />
      <nav
        ref={barRef}
        className="day-period-jump"
        aria-label={`Jump to a time of day, ${dayLabel}`}
        aria-busy={Boolean(pending)}
        data-testid="day-period-jump"
      >
        <span className="day-period-jump-day">
          {/* Desktop: "Tomorrow Sep 28", so the day being read is always named with its date */}
          <span className="day-period-jump-day-full">
            <span className="day-period-jump-day-name">{day.relative ?? day.weekday}</span>
            <span className="day-period-jump-day-date">{day.monthShort} {day.dayOfMonth}</span>
          </span>
          <span className="day-period-jump-day-short" aria-hidden="true">{shortDayLabelFor(date)}</span>
        </span>
        {DAY_PERIOD_ORDER.map((period) => {
          const { label, shortLabel } = DAY_PERIODS[period];
          const Icon = PERIOD_ICONS[period];
          const loading = pending?.period === period;
          const isCurrent = !pending && highlighted === period;
          const availability = dayPeriodAvailability(period, date, periodsByDate, dates, hasNextPage);
          const isEmpty = !loading && availability === "none";
          const ariaLabel = loading
            ? `Loading ${periodPhrase(period)}. Select again to cancel`
            : isCurrent
              ? `${label}, showing now. Select to go back to the start of the list`
              : isEmpty
                ? `No ${periodPhrase(period)} ${dayPhraseFor(date)}`
                : `Jump to ${periodPhrase(period)}, ${dayLabel}`;
          return (
            <button
              key={period}
              type="button"
              className={`day-period-jump-button is-${period}${isCurrent ? " is-current" : ""}${isEmpty ? " is-empty" : ""}`}
              onClick={() => handleTap(period)}
              aria-current={isCurrent ? "location" : undefined}
              aria-disabled={isEmpty || undefined}
              aria-label={ariaLabel}
              title={isCurrent ? "Back to the start of the list" : undefined}
              data-testid={`button-jump-${period}`}
            >
              {loading
                ? <LoaderCircle className="day-period-icon animate-spin" aria-hidden="true" />
                : <Icon className="day-period-icon" aria-hidden="true" />}
              <span className="day-period-jump-label-full">{loading ? "Loading…" : label}</span>
              <span className="day-period-jump-label-short" aria-hidden="true">{loading ? "Loading…" : shortLabel}</span>
              {isCurrent ? <X className="day-period-jump-clear" aria-hidden="true" /> : null}
            </button>
          );
        })}
        <span className="sr-only" role="status">{status}</span>
      </nav>
    </>
  );
}
